'use client';

import React, { useMemo } from 'react';
import {
	Area,
	AreaChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip as ChartsTooltip,
	TooltipProps,
	XAxis,
	YAxis,
	Bar,
	BarChart,
} from 'recharts';
import dayjs from 'dayjs';
import currencyPipe from 'src/utils/currencyPipe';
import { cm } from 'src/utils/classNames';
import {
	calculateYAxisWidth,
	getPositiveGreenColor,
	getTimeBasedTickFormatter,
	getTimeBasedXAxisTicks,
	getTooltipDate,
} from 'src/utils/graph';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { getColorVar } from 'src/utils/theme/colorVars';
import { ReferralTimePeriodOption } from 'src/components/ReferralsPageContent/consts';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import Env from 'src/environmentVariables/EnvironmentVariables';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { WithClassName } from 'src/utils/UtilityTypes';
import ChartBar from 'src/components/Charts/ChartBar';
import { ReferralChartPoint } from 'src/components/ReferralsPageContent/types';
import { TabOption } from 'src/components/primitives/Tabs/types';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import ChartTooltip from 'src/components/Charts/ChartTooltip';
import ChartActiveDot from 'src/components/Charts/ChartActiveDot';

const PERIOD_OPTIONS: TabOption<ReferralTimePeriodOption>[] = [
	{
		label: '7D',
		value: '7d',
	},
	{
		label: '30D',
		value: '30d',
	},
	{
		label: '90D',
		value: '90d',
	},
];

interface ReferralChartProps extends WithClassName {
	data: ReferralChartPoint[];
	label: string;
	type?: 'line' | 'bar';
	headerValue: number;
	selectedPeriod: ReferralTimePeriodOption;
	setSelectedPeriod: (period: ReferralTimePeriodOption) => void;
	isNotional?: boolean;
	isLoading: boolean;
}

const ReferralChart = ({
	data,
	label,
	type = 'line',
	headerValue,
	selectedPeriod,
	setSelectedPeriod,
	isNotional = false,
	isLoading,
	className,
}: ReferralChartProps) => {
	const showAccountValues = useShowAccountValues();
	const positiveColor = getPositiveGreenColor();
	const AREA_COLORS_ID = `referral-area-colors_${type}_${selectedPeriod}`;
	const isMobileScreenSize = useIsMobileScreenSize();

	const displayedData = useMemo(() => {
		if (data.length > 0) {
			return data
				.map(({ value, date }) => ({
					value,
					date: dayjs(date).valueOf(),
				}))
				.sort((a, b) => a.date - b.date);
		}

		// If no data, create a zero'd out line.
		return Array.from({ length: 10 }, (_, i) => ({
			date: dayjs().subtract(i, 'day').valueOf(),
			value: 0,
		})).sort((a, b) => a.date - b.date);
	}, [data]);

	const displayValueBigNum = BigNum.fromPrint(
		headerValue.toString(),
		isNotional ? QUOTE_PRECISION_EXP : undefined
	);

	const minValue = Math.min(...displayedData.map((point) => point.value));
	const maxValue = Math.max(...displayedData.map((point) => point.value));

	const isAllZeroValues = displayedData.every((point) => point.value === 0);

	const firstDateMs = displayedData[0]?.date ?? 0;
	const lastDateMs = displayedData[displayedData.length - 1]?.date ?? 0;

	const tickFormatterDuration = '1d';

	const dividerColor = getColorVar('stroke-divider');
	const cursorColor = getColorVar('text-secondary');

	const formatYAxisTick = (tick: number) => {
		if (!isNotional) {
			return String(tick);
		}

		if (!showAccountValues) {
			return '∗∗∗∗';
		}

		const absoluteTick = Math.abs(tick);

		const value =
			tick >= 1_000 || tick <= -1_000
				? BigNum.fromPrint(`${absoluteTick}`).toMillified()
				: absoluteTick.toLocaleString(Env.locale, {
						maximumFractionDigits: 2,
						minimumFractionDigits: 2,
					});

		return `$${value}`;
	};

	const yAxisWidth = useMemo(() => {
		const formattedLabels = [
			formatYAxisTick(minValue),
			formatYAxisTick(maxValue),
		];

		return calculateYAxisWidth(formattedLabels);
	}, [minValue, maxValue]);

	const Chart = type === 'bar' ? BarChart : AreaChart;

	return (
		<div
			className={cm(
				'flex flex-col gap-y-xs rounded-sm bg-container-bg p-lg sm:gap-y-md sm:p-2xl',
				className
			)}
		>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-x-xs text-text-default">
					<NumberDisplay
						value={displayValueBigNum}
						type={isNotional ? 'currency_signed' : 'number_signed'}
						decimals={isNotional ? 2 : undefined}
						className="typo-h5"
						loading={isLoading}
						skeletonClassName="h-5 sm:h-6"
						signDependentColor
					/>
				</div>

				{!isMobileScreenSize && (
					<Tabs
						size="sm"
						variant="secondary"
						options={PERIOD_OPTIONS}
						activeTab={selectedPeriod}
						onTabChange={setSelectedPeriod}
					/>
				)}
			</div>

			<ResponsiveContainer
				className="min-h-61 grow sm:min-h-64"
				width="100%"
				height="100%"
			>
				<Chart data={displayedData}>
					<CartesianGrid stroke={dividerColor} vertical={false} />

					<XAxis
						type="number"
						dataKey="date"
						domain={['dataMin', 'dataMax']}
						stroke={dividerColor}
						padding={type === 'bar' ? { left: 20, right: 20 } : undefined}
						tickMargin={8}
						ticks={getTimeBasedXAxisTicks(
							firstDateMs,
							lastDateMs,
							tickFormatterDuration
						)}
						interval="equidistantPreserveStart"
						tickFormatter={getTimeBasedTickFormatter()}
					/>

					<YAxis
						width={yAxisWidth}
						domain={[type === 'bar' ? 0 : 'auto', 'auto']}
						interval={type === 'bar' ? 1 : undefined}
						stroke={dividerColor}
						tickLine={false}
						tickMargin={4}
						tickFormatter={formatYAxisTick}
					/>

					<defs>
						<linearGradient id={AREA_COLORS_ID} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={positiveColor} stopOpacity={0.3} />
							<stop offset="100%" stopColor={positiveColor} stopOpacity={0} />
						</linearGradient>
					</defs>

					{type === 'line' && (
						<Area
							type="linear"
							connectNulls
							className="z-50"
							dataKey="value"
							stroke={positiveColor}
							fill={`url(#${AREA_COLORS_ID})`}
							strokeWidth={2}
							dot={false}
							activeDot={({ payload, ...rest }) => (
								<ChartActiveDot {...rest} value={payload.value} />
							)}
							isAnimationActive={false}
						/>
					)}

					{type === 'bar' && (
						<Bar
							dataKey="value"
							className="z-50"
							// @ts-ignore
							shape={({ payload, ...rest }) => (
								<ChartBar {...rest} value={payload.value} />
							)}
						/>
					)}

					{/* This is a workaround because the normal <Area /> component doesn't render if all values are 0. */}
					{isAllZeroValues && <ReferenceLine y="0" stroke={positiveColor} />}

					<ChartsTooltip
						content={(tooltipProps) => (
							<ReferralChartTooltip
								{...tooltipProps}
								isNotional={isNotional}
								label={label}
								isHourly={false}
							/>
						)}
						cursor={{
							stroke: cursorColor,
							fill: cursorColor,
							opacity: type === 'line' ? 1 : 0.1,
							strokeDasharray: type === 'line' ? 4 : 0,
							strokeWidth: type === 'line' ? 1 : 0,
						}}
					/>
				</Chart>
			</ResponsiveContainer>

			{isMobileScreenSize && (
				<Tabs
					size="sm"
					variant="secondary"
					options={PERIOD_OPTIONS}
					activeTab={selectedPeriod}
					onTabChange={setSelectedPeriod}
				/>
			)}
		</div>
	);
};

interface ReferralChartTooltipProps extends Omit<
	TooltipProps<number, string>,
	'formatter' | 'payload' | 'content'
> {
	payload?: Array<{ payload?: { value: number; date: number } }>;
	isNotional: boolean;
	label: string;
	isHourly: boolean;
}

function ReferralChartTooltip({
	active,
	payload,
	isNotional,
	label,
	isHourly,
}: ReferralChartTooltipProps) {
	const data = payload?.[0]?.payload;

	if (!active || !data) {
		return null;
	}

	const amount = data.value;

	const dateText = getTooltipDate(data.date, {
		isRelativeToToday: isHourly,
		dateWithTime: false,
	});

	const amountText = isNotional ? currencyPipe(amount, '$', 2) : String(amount);

	return (
		<ChartTooltip.Root>
			<ChartTooltip.Row>
				<span>Date</span>
				<span>
					{dateText} {!isHourly && 'GMT'}
				</span>
			</ChartTooltip.Row>

			<ChartTooltip.Row>
				<span>{label}</span>
				<span className="typo-t4 text-text-positive-green-button">
					{amountText}
				</span>
			</ChartTooltip.Row>
		</ChartTooltip.Root>
	);
}

export default React.memo(ReferralChart);
